import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import allPLData from '../data/monthly_pl_all.json';
import annualSummaryRaw from '../data/annual_summary.json';

const FinancialDataContext = createContext(null);

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const CLASS_KEYS = ['bwm', 'bts', 'bia'];

const LOCALSTORAGE_KEY = 'bcc_pl_edits';
const LOCALSTORAGE_OVERRIDES_KEY = 'bcc_pl_class_overrides';
const LOCALSTORAGE_DEFAULTS_KEY = 'bcc_pl_defaults';
const LOCALSTORAGE_DEFAULTS_OVERRIDES_KEY = 'bcc_pl_class_overrides_defaults';

// 2026 actuals cutoff: reads from QB sync if available, defaults to 3 (Jan-Mar)
function getActualsCutoff() {
  try {
    const throughMonth = parseInt(localStorage.getItem('bcc_qb_through_month'));
    if (throughMonth > 0 && throughMonth <= 12) return throughMonth;
  } catch (e) { /* ignore */ }
  return 3;
}
export const ACTUALS_CUTOFF_2026 = getActualsCutoff();

// Financial overrides loaded from config (gitignored - contains real dollar amounts)
import overridesConfig from '../config/overrides.json';
const OVERRIDE_APR_INVESTMENT_MGMT = overridesConfig.overrideAprInvestmentMgmt || 0;
// Committed tax prep computed live from BTS client data. Source of truth is
// now Supabase via ClientsProvider; we read from the in-memory cache so this
// remains a sync helper. After ClientsProvider's first async load, the cache
// has data; until then it returns 0 (acceptable — the projection rebuilds
// when global2026 is recomputed on next FDC dependency change).
import { getClientsCache } from '../utils/clientStore';
import { isClientActiveInYear, getClientRevenueComponents } from '../utils/clientDataLoader';
import { getFirmSetting, setFirmSetting } from '../utils/firmSettings';
import { subscribeFirmSettings } from '../utils/firmSettingsStore';
import { subscribeClientsCache } from '../utils/clientStore';
import { buildPLIncome } from '../revenue/plIncome.mjs';
import { buildForecastIncome } from '../revenue/forecastIncome.mjs';
function loadClientsLive() {
  return getClientsCache();
}
function computeCommittedTaxPrep(clients) {
  const list = clients || getClientsCache();
  // Year + churn aware: future-start clients contribute $0; mid-year
  // churns contribute their pro-rated base (monthly × monthsActive for
  // recurring bookkeeping, annual × monthsActive/12 for tax prep).
  return list
    .filter(c => c.division === 'bts' && c.status === 'Active' && isClientActiveInYear(c, 2026))
    .reduce((s, c) => s + getClientRevenueComponents(c, 2026).base, 0);
}

function sumCat(monthData, category) {
  if (!monthData?.[category]) return 0;
  return Object.values(monthData[category]).reduce((s, v) => s + (v || 0), 0);
}

function cloneMonth(monthObj) {
  return JSON.parse(JSON.stringify(monthObj));
}

function emptyMonth() {
  return {
    income: { commissions: 0, investment_management: 0, subscription_fees: 0, tax_preparation: 0, bookkeeping: 0, cao: 0, tax_planning: 0 },
    cogs: { payroll_taxes_direct: 0, payroll_wages_direct: 0, referral_fees: 0 },
    expenses: {
      advertising_promotional: 0, bank_service_charges: 0, business_licenses_and_permits: 0,
      client_service_fees: 0, consultant: 0, continuing_education: 0,
      dues_and_subscriptions: 0, gifts: 0, insurance_expense: 0,
      mergers_and_acquisitions: 0, meals_and_entertainment: 0, miscellaneous_expenses: 0,
      office_supplies: 0, payroll_non_employee: 0, postage_and_delivery: 0,
      professional_fees: 0, software_subscriptions: 0, telephone_expense: 0,
      travel_expense: 0, utilities: 0,
    },
  };
}

// Build the Global 2026 projection
function buildGlobal2026Projection() {
  const raw2026 = allPLData.global?.['2026'];
  const data2025 = allPLData.global?.['2025'];
  if (!raw2026 || !data2025) return raw2026;

  const actualMonths = MONTH_KEYS.slice(0, ACTUALS_CUTOFF_2026);
  const q1Inc26 = actualMonths.reduce((s, m) => s + sumCat(raw2026.months[m], 'income'), 0);
  const q1Inc25 = actualMonths.reduce((s, m) => s + sumCat(data2025.months[m], 'income'), 0);
  const growthFactor = q1Inc25 > 0 ? q1Inc26 / q1Inc25 : 1.0;

  const projectedMonths = {};
  for (let i = 0; i < 12; i++) {
    const mk = MONTH_KEYS[i];
    if (i < ACTUALS_CUTOFF_2026) {
      projectedMonths[mk] = cloneMonth(raw2026.months[mk]);
    } else {
      const base2025 = data2025.months[mk];
      if (!base2025) { projectedMonths[mk] = emptyMonth(); continue; }
      const projected = { income: {}, cogs: {}, expenses: {} };
      for (const category of ['income', 'cogs', 'expenses']) {
        for (const [key, value] of Object.entries(base2025[category] || {})) {
          if (category === 'cogs') {
            const q1c26 = actualMonths.reduce((s, m) => s + (raw2026.months[m]?.cogs?.[key] || 0), 0);
            const q1c25 = actualMonths.reduce((s, m) => s + (data2025.months[m]?.cogs?.[key] || 0), 0);
            projected.cogs[key] = Math.round(value * (q1c25 > 0 ? q1c26 / q1c25 : growthFactor));
          } else if (category === 'expenses') {
            const q1e26 = actualMonths.reduce((s, m) => s + sumCat(raw2026.months[m], 'expenses'), 0);
            const q1e25 = actualMonths.reduce((s, m) => s + sumCat(data2025.months[m], 'expenses'), 0);
            projected.expenses[key] = Math.round(value * (q1e25 > 0 ? q1e26 / q1e25 : growthFactor));
          } else {
            projected.income[key] = Math.round(value * growthFactor);
          }
        }
      }
      projectedMonths[mk] = projected;
    }
  }

  // April investment management override
  if (projectedMonths.apr) {
    projectedMonths.apr.income.investment_management = OVERRIDE_APR_INVESTMENT_MGMT;
  }

  // Global COGS payroll overrides: fixed monthly amounts Mar-Dec
  const cogsOverrideMonths = MONTH_KEYS.slice(2); // mar through dec
  for (const mk of cogsOverrideMonths) {
    if (projectedMonths[mk]) {
      projectedMonths[mk].cogs.payroll_wages_direct = overridesConfig.globalPayrollWages2026 || 0;
      projectedMonths[mk].cogs.payroll_taxes_direct = overridesConfig.globalPayrollTaxes2026 || 0;
    }
  }

  // Distribute committed tax prep
  const globalQ1TP = actualMonths.reduce((s, m) => s + (raw2026.months[m]?.income?.tax_preparation || 0), 0);
  const remainTP = computeCommittedTaxPrep() - globalQ1TP;
  if (remainTP > 0) {
    const projMk = MONTH_KEYS.slice(ACTUALS_CUTOFF_2026);
    const tp25AprDec = projMk.reduce((s, m) => s + (data2025.months[m]?.income?.tax_preparation || 0), 0);
    if (tp25AprDec > 0) {
      for (const mk of projMk) {
        const weight = (data2025.months[mk]?.income?.tax_preparation || 0) / tp25AprDec;
        projectedMonths[mk].income.tax_preparation = Math.round(remainTP * weight);
      }
    }
  }

  return { type: 'budget', months: projectedMonths };
}

// ----- CLASS SHARE RATIOS (for income and expenses) -----
function buildClassShareRatios() {
  const ratios = {};
  for (const cls of CLASS_KEYS) {
    ratios[cls] = {};
    for (const mk of MONTH_KEYS) {
      ratios[cls][mk] = { income: {}, cogs: {}, expenses: {} };
      for (const category of ['income', 'cogs', 'expenses']) {
        const globalMonth = allPLData.global?.['2025']?.months?.[mk];
        const classMonth = allPLData[cls]?.['2025']?.months?.[mk];
        if (!globalMonth?.[category] || !classMonth?.[category]) continue;
        for (const key of Object.keys(globalMonth[category] || {})) {
          const globalVal = globalMonth[category][key] || 0;
          const classVal = classMonth[category]?.[key] || 0;
          ratios[cls][mk][category][key] = globalVal !== 0 ? classVal / globalVal : 0;
        }
      }
    }
  }
  // Use 2026 actuals for Jan-Mar
  const actualMks = MONTH_KEYS.slice(0, ACTUALS_CUTOFF_2026);
  for (const cls of CLASS_KEYS) {
    for (const mk of actualMks) {
      for (const category of ['income', 'cogs', 'expenses']) {
        const globalMonth = allPLData.global?.['2026']?.months?.[mk];
        const classMonth = allPLData[cls]?.['2026']?.months?.[mk];
        if (!globalMonth?.[category] || !classMonth?.[category]) continue;
        for (const key of Object.keys(globalMonth[category] || {})) {
          const globalVal = globalMonth[category][key] || 0;
          const classVal = classMonth[category]?.[key] || 0;
          ratios[cls][mk][category][key] = globalVal !== 0 ? classVal / globalVal : 0;
        }
      }
    }
  }
  return ratios;
}

const classShareRatios = buildClassShareRatios();

// ----- DEFAULT CLASS OVERRIDES -----
// These are specific per-class values that bypass ratio-based derivation.
// Used for COGS where each division has specific payroll contracts.
function buildDefaultClassOverrides() {
  const overrides = {};
  // BWM COGS: $8,750 payroll wages, $669.38 payroll taxes (Mar-Dec)
  // BTS COGS: $19,541 payroll wages, $1,494.93 payroll taxes (Mar-Dec)
  const projMonths = MONTH_KEYS.slice(2); // mar through dec
  for (const mk of projMonths) {
    // BWM
    if (!overrides.bwm) overrides.bwm = {};
    if (!overrides.bwm[mk]) overrides.bwm[mk] = {};
    overrides.bwm[mk].cogs = {
      payroll_wages_direct: overridesConfig.bwmPayrollWages2026 || 0,
      payroll_taxes_direct: overridesConfig.bwmPayrollTaxes2026 || 0,
    };
    // BTS
    if (!overrides.bts) overrides.bts = {};
    if (!overrides.bts[mk]) overrides.bts[mk] = {};
    overrides.bts[mk].cogs = {
      payroll_wages_direct: overridesConfig.btsPayrollWages2026 || 0,
      payroll_taxes_direct: overridesConfig.btsPayrollTaxes2026 || 0,
    };
  }
  return overrides;
}

// Default division for income categories that don't have historical data.
// Used as a fallback when 2025 had no revenue in this category so the
// share ratio is 0 (e.g. tax_planning is new in 2026).
const INCOME_CATEGORY_DEFAULT_DIVISION = {
  subscription_fees: 'bwm',
  investment_management: 'bwm',
  tax_planning: 'bwm',
  tax_preparation: 'bts',
  commissions: 'bia',
};

// Derive a class's 2026 data from Global using ratios + overrides
function deriveClassFromGlobal(classKey, globalData, overrides) {
  if (!globalData?.months) return null;
  const ratios = classShareRatios[classKey];
  if (!ratios) return null;

  const months = {};
  for (const mk of MONTH_KEYS) {
    const globalMonth = globalData.months[mk];
    if (!globalMonth) { months[mk] = emptyMonth(); continue; }

    const derived = { income: {}, cogs: {}, expenses: {} };
    const monthOverrides = overrides?.[classKey]?.[mk] || {};

    for (const category of ['income', 'cogs', 'expenses']) {
      for (const [key, globalVal] of Object.entries(globalMonth[category] || {})) {
        // Check if there's a specific override for this class/month/category/key
        const override = monthOverrides[category]?.[key];
        if (override !== undefined) {
          derived[category][key] = override;
        } else {
          // Fall back to ratio-based derivation
          const ratio = ratios[mk]?.[category]?.[key];
          if (ratio != null && ratio > 0) {
            derived[category][key] = Math.round(globalVal * ratio);
          } else if (category === 'income' && INCOME_CATEGORY_DEFAULT_DIVISION[key]) {
            // No historical ratio - use default division mapping for income
            // (e.g. tax_planning is new in 2026, route 100% to BWM)
            derived[category][key] = INCOME_CATEGORY_DEFAULT_DIVISION[key] === classKey
              ? Math.round(globalVal)
              : 0;
          } else {
            derived[category][key] = 0;
          }
        }
      }
    }
    months[mk] = derived;
  }

  return { type: 'budget', months };
}

// ----- LOCALSTORAGE PERSISTENCE -----
function loadUserDefaults() {
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_DEFAULTS_KEY);
    if (stored) { const p = JSON.parse(stored); if (p?.months) return p; }
  } catch (e) { /* ignore */ }
  return buildGlobal2026Projection();
}

function loadUserDefaultOverrides() {
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_DEFAULTS_OVERRIDES_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) { /* ignore */ }
  return buildDefaultClassOverrides();
}

// P&L overrides + class overrides now live in firm_settings (Supabase).
// FirmSettingsProvider hydrates the cache on app boot. Until then the
// cache is empty — we fall back to localStorage during the brief boot
// window so the UI doesn't flash to defaults.
function loadGlobalData() {
  const fromSettings = getFirmSetting('plOverridesGlobal', null);
  if (fromSettings?.months) return fromSettings;
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_KEY);
    if (stored) { const p = JSON.parse(stored); if (p?.months) return p; }
  } catch (e) { /* ignore */ }
  return loadUserDefaults();
}

function loadClassOverrides() {
  const fromSettings = getFirmSetting('plOverridesClasses', null);
  if (fromSettings) return fromSettings;
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_OVERRIDES_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) { /* ignore */ }
  return loadUserDefaultOverrides();
}

// Per-class QB actuals (real per-division numbers for closed months, pulled
// from QuickBooks class tracking by QBSyncButton). Shape:
//   { bwm: { months: { jan: {income,cogs,expenses}, ... } }, bts: {...}, bia: {...} }
// Only closed months are populated. Used to OVERRIDE the ratio-derived class
// values for actual months (projected months keep ratio derivation).
function loadClassActuals() {
  const fromSettings = getFirmSetting('plClassActuals', null);
  if (fromSettings) return fromSettings;
  try {
    const stored = localStorage.getItem('bcc_qb_class_actuals');
    if (stored) return JSON.parse(stored);
  } catch (e) { /* ignore */ }
  return null;
}

// Overlay real per-class QB actuals onto the ratio-derived class months.
// For each ACTUAL month (index < cutoff) that has QB class actuals, replace
// the ratio-derived numbers with the real ones. Projected months are left as
// the ratio derivation. This is what makes actual months show true QB
// per-class income/expenses instead of a 2025-ratio estimate.
function overlayClassActuals(derived, classActualsForClass, cutoff) {
  if (!derived?.months || !classActualsForClass?.months) return derived;
  const months = { ...derived.months };
  for (let i = 0; i < cutoff; i++) {
    const mk = MONTH_KEYS[i];
    const actual = classActualsForClass.months[mk];
    if (actual && (actual.income || actual.cogs || actual.expenses)) {
      months[mk] = {
        income: { ...(derived.months[mk]?.income || {}), ...actual.income },
        cogs: { ...(derived.months[mk]?.cogs || {}), ...actual.cogs },
        expenses: { ...(derived.months[mk]?.expenses || {}), ...actual.expenses },
      };
    }
  }
  return { ...derived, months };
}

// Replace each month's income with the engine's per-class income; keep the
// ratio-derived COGS and expenses (and their QB actuals overlay) untouched.
function applyClassIncome(derived, classIncome) {
  if (!derived?.months || !classIncome?.months) return derived;
  const months = {};
  for (const mk of MONTH_KEYS) {
    const d = derived.months[mk] || emptyMonth();
    const inc = classIncome.months[mk]?.income || {};
    months[mk] = { ...d, income: { ...inc } };
  }
  return { ...derived, months };
}

// Scale every value in a cogs/expenses category map by a factor (used to grow
// 2026 costs into the 2027 forecast).
function scaleCategory(cats, factor) {
  const out = {};
  for (const [k, v] of Object.entries(cats || {})) out[k] = Math.round((Number(v) || 0) * factor);
  return out;
}

// Compute annual summary row for 2026
function compute2026Annual(monthlyData) {
  if (!monthlyData?.months) return null;
  let totalRevenue = 0, totalCogs = 0, totalExpenses = 0;
  for (const mk of MONTH_KEYS) {
    const md = monthlyData.months[mk];
    totalRevenue += sumCat(md, 'income');
    totalCogs += sumCat(md, 'cogs');
    totalExpenses += sumCat(md, 'expenses');
  }
  return {
    year: 2026, revenue: totalRevenue, net_income: totalRevenue - totalCogs - totalExpenses,
    total_cogs: totalCogs, total_expenses: totalExpenses, projected: true,
  };
}

// Forecast scenario: conservative / normal / aggressive
// Stored globally so all pages use the same setting
const FORECAST_SCENARIO_KEY = 'bcc_forecast_scenario';
const VALID_SCENARIOS = ['conservative', 'normal', 'aggressive'];

function loadForecastScenario() {
  const fromSettings = getFirmSetting('forecastScenario', null);
  if (VALID_SCENARIOS.includes(fromSettings)) return fromSettings;
  try {
    const stored = localStorage.getItem(FORECAST_SCENARIO_KEY);
    if (VALID_SCENARIOS.includes(stored)) return stored;
  } catch (e) { /* ignore */ }
  return 'normal';
}

// Multipliers applied to forecast layers per scenario.
// plRevenueMultiplier: overall multiplier applied to projected (Apr-Dec) income
// lines in the P&L view. Lower than the new-business multipliers because most
// of the P&L is committed recurring revenue.
export const SCENARIO_CONFIG = {
  conservative: {
    label: 'Conservative',
    pipelineMultiplier: 0.7,
    pipelineMinStageOrder: 4, // Strategy Complete or later
    velocityMultiplier: 0.5,
    conversionAdjust: -0.1,
    churnMultiplier: 1.5,
    plRevenueMultiplier: 0.92, // ~ -8% on projected revenue
    color: 'text-rose',
  },
  normal: {
    label: 'Normal',
    pipelineMultiplier: 1.0,
    pipelineMinStageOrder: 1, // All active stages
    velocityMultiplier: 1.0,
    conversionAdjust: 0,
    churnMultiplier: 1.0,
    plRevenueMultiplier: 1.0,
    color: 'text-navy',
  },
  aggressive: {
    label: 'Aggressive',
    pipelineMultiplier: 1.2,
    pipelineMinStageOrder: 1,
    velocityMultiplier: 1.3,
    conversionAdjust: 0.1,
    churnMultiplier: 0.7,
    plRevenueMultiplier: 1.08, // ~ +8% on projected revenue
    color: 'text-deep-teal',
  },
};

export function FinancialDataProvider({ children }) {
  const [global2026, setGlobal2026] = useState(loadGlobalData);
  const [classOverrides, setClassOverrides] = useState(loadClassOverrides);
  const [classActuals, setClassActuals] = useState(loadClassActuals);
  const [forecastScenario, setForecastScenarioState] = useState(loadForecastScenario);
  // 2027 forecast: grow 2026 COGS + expenses by this rate (default 10%). Income
  // comes from the forecast engine; this only seeds the cost side. Shared via
  // firm_settings so every user sees the same forecast.
  const [forecastExpenseGrowth, setForecastExpenseGrowthState] = useState(() => {
    const fromSettings = getFirmSetting('forecast2027ExpenseGrowth', null);
    if (typeof fromSettings === 'number' && isFinite(fromSettings)) return fromSettings;
    return 0.10;
  });
  // Bumped whenever the clients cache changes (set by ClientsProvider after
  // its async load completes, or after any edit). Used as a dep on the KPI
  // useMemo so retention etc recompute when client data arrives or changes —
  // without this, the KPI gets stuck at "100% retention" because it
  // computed before clients loaded.
  const [clientsTick, setClientsTick] = useState(0);
  useEffect(() => subscribeClientsCache(() => setClientsTick(t => t + 1)), []);

  // Re-hydrate when FirmSettingsProvider's async load completes — we may have
  // started with localStorage shadow values that need to be overridden by what
  // the API returns (which is the source of truth).
  useEffect(() => {
    const off = subscribeFirmSettings((fs) => {
      if (fs.plOverridesGlobal && fs.plOverridesGlobal.months) setGlobal2026(fs.plOverridesGlobal);
      if (fs.plOverridesClasses) setClassOverrides(fs.plOverridesClasses);
      if (fs.plClassActuals) setClassActuals(fs.plClassActuals);
      if (VALID_SCENARIOS.includes(fs.forecastScenario)) setForecastScenarioState(fs.forecastScenario);
      if (typeof fs.forecast2027ExpenseGrowth === 'number' && isFinite(fs.forecast2027ExpenseGrowth)) setForecastExpenseGrowthState(fs.forecast2027ExpenseGrowth);
    });
    return off;
  }, []);

  const setForecastScenario = useCallback((scenario) => {
    if (!VALID_SCENARIOS.includes(scenario)) return;
    setForecastScenarioState(scenario);
    setFirmSetting('forecastScenario', scenario);
    // Keep localStorage shadow so a deploy that fails to load firm settings
    // still surfaces the user's last choice.
    try { localStorage.setItem(FORECAST_SCENARIO_KEY, scenario); } catch (e) { /* ignore */ }
  }, []);

  const setForecastExpenseGrowth = useCallback((rate) => {
    const r = Number(rate);
    if (!isFinite(r)) return;
    setForecastExpenseGrowthState(r);
    setFirmSetting('forecast2027ExpenseGrowth', r);
  }, []);

  // Update a cell and persist (Supabase via firmSettings + local shadow)
  const updateCell = useCallback((classKey, month, category, key, value) => {
    if (classKey === 'global') {
      setGlobal2026((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        updated.months[month][category][key] = value;
        setFirmSetting('plOverridesGlobal', updated);
        try { localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(updated)); } catch (e) { /* ignore */ }
        return updated;
      });
    } else {
      setClassOverrides((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        if (!updated[classKey]) updated[classKey] = {};
        if (!updated[classKey][month]) updated[classKey][month] = {};
        if (!updated[classKey][month][category]) updated[classKey][month][category] = {};
        updated[classKey][month][category][key] = value;
        setFirmSetting('plOverridesClasses', updated);
        try { localStorage.setItem(LOCALSTORAGE_OVERRIDES_KEY, JSON.stringify(updated)); } catch (e) { /* ignore */ }
        return updated;
      });
    }
  }, []);

  // Rebuild the projected-month income from live client records and persist.
  // Reuses the same builder the manual "Sync Client Revenue" button uses, so
  // both paths produce identical numbers. Writing plOverridesGlobal updates
  // global2026 here AND propagates to every other user via firm_settings —
  // and because FinancialDataProvider subscribes to plOverridesGlobal
  // (above), no page reload is needed; the dashboard recomputes live.
  //
  // Save current state as new defaults
  const saveAsDefault = useCallback(() => {
    try {
      localStorage.setItem(LOCALSTORAGE_DEFAULTS_KEY, JSON.stringify(global2026));
      localStorage.setItem(LOCALSTORAGE_DEFAULTS_OVERRIDES_KEY, JSON.stringify(classOverrides));
    } catch (e) { /* ignore */ }
  }, [global2026, classOverrides]);

  // Reset to saved defaults
  const resetProjections = useCallback(() => {
    const defaults = loadUserDefaults();
    const defaultOverrides = loadUserDefaultOverrides();
    setGlobal2026(defaults);
    setClassOverrides(defaultOverrides);
    // Clear both the firm-wide overrides AND the localStorage shadow so a
    // refresh truly resets back to defaults.
    setFirmSetting('plOverridesGlobal', null);
    setFirmSetting('plOverridesClasses', null);
    try {
      localStorage.removeItem(LOCALSTORAGE_KEY);
      localStorage.removeItem(LOCALSTORAGE_OVERRIDES_KEY);
    } catch (e) { /* ignore */ }
  }, []);

  // Won-deals overlay removed (July 2026): the revenue engine below
  // (buildPLIncome) is the single, complete income source, so there is no
  // separate overlay to store, add, or reconcile. Closed-Won now writes a
  // real client record (see ClosedWonModal + closedWonToClient.mjs), which the
  // engine picks up like any other engagement.

  // New revenue engine: a single, COMPLETE income source (clients + not-yet-client
  // Closed-Won), reconciled with QuickBooks actuals. Replaces the old base
  // projection + won-deals overlay for INCOME only. COGS and expenses are untouched.
  const engineIncome = useMemo(() => {
    try {
      return buildPLIncome(loadClientsLive(), classActuals, 2026);
    } catch (e) {
      console.error('[FinancialData] engine income failed:', e);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classActuals, clientsTick]);

  // Global P&L with its income side swapped to the engine; COGS + expenses kept.
  const reconciledGlobal = useMemo(() => {
    if (!engineIncome?.global?.months || !global2026?.months) return global2026;
    const months = {};
    for (const mk of MONTH_KEYS) {
      const base = global2026.months[mk] || emptyMonth();
      const inc = engineIncome.global.months[mk]?.income;
      months[mk] = { ...base, income: inc ? { ...inc } : base.income };
    }
    return { ...global2026, months };
  }, [global2026, engineIncome]);

  // Build editableByClass: Global is master. Each class takes COGS/expenses from
  // the ratio derivation (with QB per-class actuals overlaid on closed months) and
  // its INCOME from the engine's per-class reconciled numbers.
  const editableByClass = useMemo(() => {
    const result = { global: reconciledGlobal };
    for (const cls of CLASS_KEYS) {
      const ratioDerived = deriveClassFromGlobal(cls, global2026, classOverrides);
      const withActuals = overlayClassActuals(ratioDerived, classActuals?.[cls], ACTUALS_CUTOFF_2026);
      result[cls] = engineIncome?.byClass?.[cls]
        ? applyClassIncome(withActuals, engineIncome.byClass[cls])
        : withActuals;
    }
    return result;
  }, [reconciledGlobal, global2026, classOverrides, classActuals, engineIncome]);

  // Forward-year (2027) forecast P&L. Income from the forecast engine (recurring
  // + AUM continue and annualize; tax prep + commissions mirror 2026; tax
  // planning starts empty). COGS + expenses are seeded from 2026 grown by the
  // chosen rate. Read-only in the P&L view.
  const forecast2027 = useMemo(() => {
    let fc;
    try {
      fc = buildForecastIncome(loadClientsLive(), classActuals, 2027);
    } catch (e) {
      console.error('[FinancialData] forecast income failed:', e);
      return null;
    }
    const factor = 1 + (Number(forecastExpenseGrowth) || 0);
    const grow = (base, incomeSrc) => {
      const months = {};
      for (const mk of MONTH_KEYS) {
        const b = base?.months?.[mk] || emptyMonth();
        const inc = incomeSrc?.months?.[mk]?.income || {};
        months[mk] = {
          income: { ...inc },
          cogs: scaleCategory(b.cogs, factor),
          expenses: scaleCategory(b.expenses, factor),
        };
      }
      return { type: 'forecast', months };
    };
    const result = { global: grow(reconciledGlobal, fc.global) };
    for (const cls of CLASS_KEYS) result[cls] = grow(editableByClass[cls], fc.byClass[cls]);
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconciledGlobal, editableByClass, classActuals, clientsTick, forecastExpenseGrowth]);


  // Computed annual summary. The 2026 income already includes Closed-Won via
  // the revenue engine, so there is nothing to add on top.
  const annualSummary = useMemo(() => {
    const historical = annualSummaryRaw.annual_summary.filter(row => row.year !== 2026);
    const live2026 = compute2026Annual(reconciledGlobal);
    return live2026 ? [...historical, live2026] : historical;
  }, [reconciledGlobal]);

  // Live KPI (committed revenue; scenario does NOT apply here. The P&L numbers
  // are already booked/committed - scenario only affects the forecast OVERLAY
  // (new business from pipeline + velocity).
  const kpi = useMemo(() => {
    const live2026 = compute2026Annual(reconciledGlobal);

    const scenarioCfg = SCENARIO_CONFIG[forecastScenario] || SCENARIO_CONFIG.normal;
    const base = annualSummaryRaw.kpi;

    // FP Client Retention — COHORT method (classic RIA / wealth-firm metric).
    //
    // Definition: of the FP-tier BWM clients we started the year with, how
    // many are still active today? New wins this year do NOT enter the
    // denominator (they aren't part of the start-of-year cohort), and they
    // don't dilute the churn rate either way.
    //
    // Cohort membership: clients who EXISTED on Jan 1, 2026. We derive that
    // dynamically rather than storing a snapshot, by filtering on:
    //   - startDate < cohort cutoff (or missing — assumed long-tenured)
    //   - status = Active, OR
    //   - status = Churned AND churnedYear === current year (left mid-year)
    //
    // Clients with status='Churned' AND churnedYear < current year are
    // excluded — they left before the cohort year began.
    //
    // The fp_clients_total field still reports current active count (used
    // in the subtitle to surface mid-year growth: "5 churned of 61 cohort
    // | 58 active today").
    const COHORT_YEAR = 2026;
    const cohortCutoff = new Date(`${COHORT_YEAR}-01-01T00:00:00Z`).getTime();
    const fpTiers = ['guided-wealth', 'total-wealth', 'tax-plan'];
    const bwmClients = loadClientsLive().filter(c => c.division === 'bwm');
    const fpBwm = bwmClients.filter(c => fpTiers.includes(c.serviceTier));

    // A client is in the cohort if they existed on Jan 1 of the cohort year.
    // Missing startDate is treated as "long-tenured" (in cohort) to avoid
    // under-counting legacy clients seeded without a start date.
    //
    // Note: comparison is `<=` not `<`. A startDate of exactly Jan 1 of the
    // cohort year is the migration/default placeholder pattern (long-tenured
    // clients whose true start date wasn't captured). Treating Jan 1 as "in
    // cohort" matches reality. The edge case of a genuine Jan-1 new win is
    // rare; if it ever happens, the small misclassification is acceptable.
    function wasInCohort(c) {
      const startMs = c.startDate ? new Date(c.startDate).getTime() : NaN;
      const startedBeforeCohort = !Number.isFinite(startMs) || startMs <= cohortCutoff;
      if (!startedBeforeCohort) return false;
      if (c.status === 'Active') return true;
      if (c.status === 'Churned' && Number(c.churnedYear) === COHORT_YEAR) return true;
      // Churned in a prior year — not in this year's cohort.
      return false;
    }
    const fpCohort = fpBwm.filter(wasInCohort);
    const fpOriginal  = fpCohort.length;                                    // start-of-year cohort size
    const fpRetained  = fpCohort.filter(c => c.status === 'Active').length; // cohort still active
    const fpTotal     = fpBwm.filter(c => c.status === 'Active').length;   // all active today (cohort + mid-year wins)

    return {
      ...base,
      projected_revenue_2026: live2026?.revenue || base.projected_revenue_2026,
      projected_net_income_2026: live2026?.net_income || base.projected_net_income_2026,
      fp_clients_original: fpOriginal,
      fp_clients_retained: fpRetained,
      fp_clients_total: fpTotal,
      scenarioLabel: scenarioCfg.label,
    };
  // clientsTick is intentionally in the dep array — it bumps when the
  // clients cache changes so retention re-counts churned clients.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconciledGlobal, forecastScenario, clientsTick]);

  const targets = annualSummaryRaw.targets;

  const value = useMemo(() => ({
    editableByClass, updateCell, resetProjections, saveAsDefault,
    allPLData, annualSummary, targets, kpi,
    global2026Monthly: reconciledGlobal,
    forecast2027, forecastExpenseGrowth, setForecastExpenseGrowth,
    forecastScenario, setForecastScenario,
  }), [editableByClass, updateCell, resetProjections, saveAsDefault, annualSummary, targets, kpi, reconciledGlobal, forecast2027, forecastExpenseGrowth, setForecastExpenseGrowth, forecastScenario, setForecastScenario]);

  return (
    <FinancialDataContext.Provider value={value}>
      {children}
    </FinancialDataContext.Provider>
  );
}

export function useFinancialData() {
  const ctx = useContext(FinancialDataContext);
  if (!ctx) throw new Error('useFinancialData must be used within FinancialDataProvider');
  return ctx;
}
